import { useEffect, useMemo, ReactElement, ReactNode } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Table, useMagneticTable, MdsTableColumn, getSelectionColumn } from '@magnetic/table'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Select } from '@magnetic/select'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Spinner } from '@magnetic/spinner'
import { Input } from '@magnetic/input'
import { Button } from '@magnetic/button'
import { Drawer } from '@magnetic/drawer'
import { Divider } from '@magnetic/divider'
import { Funnel, XCircle, ClockCounterClockwise } from '@/lib/icons'
import { Positive, Negative } from '@magnetic/icons'
import { timestampToDate } from '@/lib/protobuf-utils'
import { useAuditFiltersStore } from '@/stores'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import type { AuditLogEntry, GetAuditLogsResponse } from '@/gen/catpocs/v1/admin_pb'
import type { getAuditLogs } from '@/lib/api'

interface AuditLogRow {
  id: string
  timestamp: string
  userId: string
  userEmail: string
  action: string
  resourceType: string
  resourceName: string
  ipAddress: string
  status: string
}

interface AuditLogsTableProps {
  queryFn: typeof getAuditLogs
  title?: string
  description?: string
  selectableRows?: boolean
  configComponents?: ReactElement | null
  bulkActions?: (selectedCount: number, selectedRows: AuditLogRow[]) => ReactNode
  initialPageSize?: number
  projectId?: string // Optional: filter by specific project
  onExport?: () => void
}

export function AuditLogsTable({
  queryFn,
  title = 'Audit Logs',
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  initialPageSize = 50,
  projectId,
}: AuditLogsTableProps) {
  const auditFilters = useAuditFiltersStore()

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      auditFilters.setDebouncedSearch(auditFilters.searchTerm)
      auditFilters.setCurrentPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [auditFilters.searchTerm, auditFilters])

  // Fetch data with all filters
  const pageSize = initialPageSize
  const skip = auditFilters.currentPage * pageSize
  const { data: rawData, isLoading, error } = useConnectQuery(
    queryFn,
    {
      projectId: projectId || '',
      search: auditFilters.debouncedSearch,
      action: auditFilters.action,
      resourceType: auditFilters.resourceType,
      status: auditFilters.status,
      startTime: auditFilters.startDate ? timestampFromDate(new Date(auditFilters.startDate)) : undefined,
      endTime: auditFilters.endDate ? timestampFromDate(new Date(auditFilters.endDate)) : undefined,
      pageSize: pageSize,
      skip: skip,
    },
    {
      refetchInterval: 60000, // Refresh every minute
    }
  )

  const data = rawData as GetAuditLogsResponse | undefined
  const totalCount = data?.totalCount ?? 0

  // Transform data for table
  const tableData: AuditLogRow[] = useMemo(() => {
    const logs: AuditLogEntry[] = data?.logs ?? []
    if (logs.length === 0) return []
    return logs.map((log) => ({
      id: log.id || '',
      timestamp: log.timestamp ? (timestampToDate(log.timestamp)?.toLocaleString() || 'Invalid Date') : '',
      userId: log.userId || '',
      userEmail: log.userEmail || '',
      action: log.action || '',
      resourceType: log.resourceType || '',
      resourceName: log.resourceName || '',
      ipAddress: log.ipAddress || '',
      status: log.status || 'success',
    }))
  }, [data])

  // Define columns
  const columns: MdsTableColumn<AuditLogRow>[] = useMemo(() => {
    const cols: MdsTableColumn<AuditLogRow>[] = [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'userEmail',
        header: 'User',
        cell: (info) => (
          <Flex direction="vertical" gap={2}>
            <Text size="p3" weight="semi-bold">{info.getValue() as string}</Text>
            <Text size="p4" color="light">{info.row.original.ipAddress}</Text>
          </Flex>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: (info) => {
          const action = info.getValue() as string
          const color = action.includes('delete') ? 'var(--mds-color-theme-text-negative-normal)' :
            action.includes('create') ? 'var(--mds-color-theme-text-positive-normal)' :
              action.includes('update') ? 'var(--mds-color-theme-text-information-normal)' :
                'var(--mds-color-theme-text-normal)'
          return (
            <Text size="p3" weight="semi-bold" style={{ color }}>
              {action}
            </Text>
          )
        },
      },
      {
        accessorKey: 'resourceType',
        header: 'Resource Type',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'resourceName',
        header: 'Resource',
        cell: (info) => <Text size="p3" weight="semi-bold">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() as string
          return status === 'success' ? (
            <Positive hasBackground />
          ) : (
            <Negative hasBackground />
          )
        },
      },
    ]

    if (selectableRows) {
      return [getSelectionColumn<AuditLogRow>(), ...cols]
    }
    return cols
  }, [selectableRows])

  // Use Magnetic table hook
  const { table } = useMagneticTable({
    data: tableData,
    columns,
  })

  const showReset = auditFilters.debouncedSearch !== '' || auditFilters.action !== '' || auditFilters.resourceType !== '' || auditFilters.status !== '' || auditFilters.startDate !== '' || auditFilters.endDate !== ''
  const handleReset = () => {
    auditFilters.resetFilters()
  }

  if (error) {
    return (
      <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
        <XCircle size={48} />
        <Heading size="sub-section">Failed to load audit logs</Heading>
        <Text color="light">{error instanceof Error ? error.message : 'An error occurred'}</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap={16} style={{ width: '100%' }}>
      {(title || description) && (
        <Flex direction="vertical" gap={4}>
          {title && <Heading size="h2">{title}</Heading>}
          {description && <Text color="light">{description}</Text>}
        </Flex>
      )}

      <FilterBar
        searchBar={
          <Flex align="center" gap={12} style={{ flex: 1 }}>
            <Flex style={{ flex: 1 }}>
              <Search
                label="Search logs"
                placeholder="Search by resource name or user email..."
                value={auditFilters.searchTerm}
                onChange={(e) => auditFilters.setSearchTerm(e.target.value)}
              />
            </Flex>
            <Button
              kind="tertiary"
              size="md"
              icon={<Funnel size={16} weight="bold" />}
              onClick={() => auditFilters.toggleDrawer()}
            >
              Filters
            </Button>
          </Flex>
        }
        results={totalCount}
        isLoadingResults={isLoading}
        showResetButton={showReset}
        onReset={handleReset}
        configComponents={configComponents ?? undefined}
      >
        <Flex />
      </FilterBar>

      {/* Filters Drawer */}
      <Drawer
        isOpen={auditFilters.isDrawerOpen}
        onClose={() => auditFilters.toggleDrawer()}
        size="md"
      >
        <Drawer.Content>
          <Flex direction="vertical" gap={24} style={{ padding: '24px' }}>
            <Flex direction="vertical" gap={8}>
              <Heading size="section">Filters</Heading>
              <Text size="p3" color="light">
                Filter audit logs by action, resource type, status, and date range
              </Text>
            </Flex>

            <Divider />

            <Flex direction="vertical" gap={16}>
              <Select
                label="Action"
                placeholder="Select action"
                value={auditFilters.action}
                onChange={(value) => {
                  auditFilters.setAction(value as string)
                }}
                options={[
                  { label: 'All Actions', value: '' },
                  { label: 'Create', value: 'create' },
                  { label: 'Update', value: 'update' },
                  { label: 'Delete', value: 'delete' },
                  { label: 'Login', value: 'login' },
                ]}
              />

              <Select
                label="Resource Type"
                placeholder="Select resource type"
                value={auditFilters.resourceType}
                onChange={(value) => {
                  auditFilters.setResourceType(value as string)
                }}
                options={[
                  { label: 'All Types', value: '' },
                  { label: 'Project', value: 'project' },
                  { label: 'Application', value: 'application' },
                  { label: 'Repository', value: 'repository' },
                  { label: 'User', value: 'user' },
                ]}
              />

              <Select
                label="Status"
                placeholder="Select status"
                value={auditFilters.status}
                onChange={(value) => {
                  auditFilters.setStatus(value as string)
                }}
                options={[
                  { label: 'All Statuses', value: '' },
                  { label: 'Success', value: 'success' },
                  { label: 'Failure', value: 'failure' },
                ]}
              />

              <Divider />

              <Heading size="sub-section">Date Range</Heading>

              <Input
                type="datetime-local"
                label="Start Date"
                value={auditFilters.startDate}
                onChange={(e) => {
                  auditFilters.setDateRange(e.target.value, auditFilters.endDate)
                }}
              />

              <Input
                type="datetime-local"
                label="End Date"
                value={auditFilters.endDate}
                onChange={(e) => {
                  auditFilters.setDateRange(auditFilters.startDate, e.target.value)
                }}
              />
            </Flex>

            <Divider />

            <Flex justify="space-between" align="center">
              <Button
                kind="tertiary"
                onClick={() => {
                  auditFilters.resetFilters()
                }}
              >
                Clear All Filters
              </Button>
              <Button
                kind="primary"
                onClick={() => auditFilters.toggleDrawer()}
              >
                Apply Filters
              </Button>
            </Flex>
          </Flex>
        </Drawer.Content>
      </Drawer>

      {selectableRows && bulkActions && table.getSelectedRowModel().rows.length > 0 && (
        <Table.BulkActionBar>
          <Flex align="center" gap={12}>
            {bulkActions(
              table.getSelectedRowModel().rows.length,
              table.getSelectedRowModel().rows.map((row) => row.original)
            )}
          </Flex>
        </Table.BulkActionBar>
      )}

      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: 48 }}>
          <Spinner size="lg" />
        </Flex>
      ) : tableData.length === 0 ? (
        <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
          <ClockCounterClockwise size={48} style={{ opacity: 0.3 }} />
          <Heading size="sub-section">No audit logs found</Heading>
          <Text color="light">
            {auditFilters.debouncedSearch || auditFilters.action || auditFilters.resourceType || auditFilters.status || auditFilters.startDate || auditFilters.endDate
              ? 'Try adjusting your filters'
              : 'No audit events have been recorded yet'}
          </Text>
        </Flex>
      ) : (
        <Table
          table={table}
          paginationControls={totalCount > pageSize ? {} : undefined}
        />
      )}
    </Flex>
  )
}
